import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, FileJson, KeyRound } from 'lucide-react';
import { useSessao } from '../auth/SessaoContext';
import { BotaoLink, cx } from '../componentes/ui';
import {
  GRUPOS_API,
  URL_BASE,
  type Operacao,
  type Parametro,
} from '../conteudo/api';
import { registrarVisita } from '../util/visita';
import { Cabecalho } from './publico/Cabecalho';
import { Rodape } from './publico/Rodape';
import base from './publico/publico.module.css';
import pv from './Privacidade.module.css';
import css from './Desenvolvedores.module.css';

const SECOES_FIXAS = [
  { id: 'comecando', titulo: 'Começando' },
  { id: 'precos', titulo: 'Preços na API' },
  { id: 'limites-e-erros', titulo: 'Limites e erros' },
  { id: 'recebendo-webhooks', titulo: 'Recebendo webhooks' },
];

/**
 * Documentação da API para desenvolvedores.
 *
 * A referência sai de `conteudo/api.ts` — o mesmo catálogo que gera o
 * `openapi.json` e que o teste e2e confere contra as rotas do servidor. Texto
 * escrito à mão aqui é só o que não é rota: autenticação, preço, limites e
 * como validar um webhook.
 */
export function Desenvolvedores() {
  const { usuario } = useSessao();

  useEffect(() => {
    registrarVisita();
  }, []);

  const indice = [
    ...SECOES_FIXAS,
    ...GRUPOS_API.map((g) => ({ id: g.id, titulo: g.titulo })),
  ];

  return (
    <div className={base.pagina}>
      <Cabecalho />

      <header className={pv.abertura}>
        <div className={base.conteudo}>
          <nav aria-label="Você está em" className={pv.migalhas}>
            <Link to="/">Início</Link>
            <ChevronRight aria-hidden="true" />
            <span aria-current="page">Desenvolvedores</span>
          </nav>
          <h1 className={pv.titulo}>API do BAIXA XML</h1>
          <p className={css.chamada}>
            Integre o download de XML de NF-e ao seu sistema: consulte uma chave
            e receba o XML na resposta, mande lotes de milhares de chaves, leia
            as notas que a captura automática recebe da SEFAZ e seja avisado por
            webhook. Para ERPs, sistemas contábeis e quem quer revender — o
            preço cai com o volume.
          </p>
          <div className={css.acoes}>
            <BotaoLink
              to={usuario ? '/painel/configuracoes' : '/criar-conta'}
              aparencia="destaque"
              icone={<KeyRound />}
            >
              {usuario ? 'Gerar chave de API' : 'Criar conta e gerar a chave'}
            </BotaoLink>
            <a className={css.linkSpec} href="/openapi.json">
              <FileJson aria-hidden="true" /> Especificação OpenAPI
            </a>
          </div>
        </div>
      </header>

      <div className={cx(base.conteudo, pv.grade)}>
        <aside className={pv.lateral}>
          <nav aria-label="Nesta página" className={pv.sumario}>
            <span className={pv.sumarioTitulo}>Nesta página</span>
            {indice.map((i) => (
              <a key={i.id} href={`#${i.id}`}>
                {i.titulo}
              </a>
            ))}
          </nav>
        </aside>

        <main className={pv.documento}>
          <Secao id="comecando" titulo="Começando">
            <p className={pv.p}>
              Todas as rotas ficam em <code>{URL_BASE}</code> e respondem JSON.
              Autentique com uma <b>chave de API</b>, criada no painel em{' '}
              <i>Configurações → Chaves de API</i>, no cabeçalho{' '}
              <code>x-api-key</code>. A chave age em nome da sua conta e usa o
              mesmo saldo do painel: guarde-a no servidor, nunca no navegador ou
              no aplicativo do usuário final.
            </p>
            <Codigo>{`curl ${URL_BASE}/nfe/consulta/35241012345678000190550010000123451234567892 \\
  -H "x-api-key: bxk_SUA_CHAVE"`}</Codigo>
            <p className={pv.p}>
              É preciso ter o e-mail da conta confirmado. As rotas que gastam
              crédito devolvem <code>400</code> com{' '}
              <code>error: &quot;SaldoInsuficiente&quot;</code> e{' '}
              <code>faltamCentavos</code> quando o saldo não cobre — a recarga é
              por Pix, no painel.
            </p>
          </Secao>

          <Secao id="precos" titulo="Preços na API">
            <ul className={pv.lista}>
              <li>
                <b>Por XML entregue</b>, o mesmo preço do painel. Chave não
                encontrada e falha da fonte não são cobradas.
              </li>
              <li>
                <b>A faixa usa o volume da conta</b>: o tamanho do pedido somado
                aos XMLs entregues nos últimos 30 dias. Quem consulta uma chave
                por vez, mas muitas no mês, paga o mesmo que pagaria em lote.
                Veja as faixas em <Link to="/#precos">Preços</Link> ou em{' '}
                <code>GET /creditos/precos</code>.
              </li>
              <li>
                <b>Consulta repetida não paga de novo</b>: a mesma chave pela
                mesma conta em até 24 horas sai sem custo.
              </li>
              <li>
                Antes de mandar um lote, <code>GET /creditos/orcamento</code>{' '}
                diz o preço e o total exatos.
              </li>
            </ul>
          </Secao>

          <Secao id="limites-e-erros" titulo="Limites e erros">
            <p className={pv.p}>
              Até <b>100 requisições por minuto</b> por chave de API, e o mesmo
              por endereço de origem — vale o que bater primeiro. Acima disso a
              resposta é <code>429</code>; espere e tente de novo. Para volume,
              prefira lotes à consulta avulsa: um lote de 100 mil chaves é uma
              requisição só.
            </p>
            <p className={pv.p}>Erros vêm sempre neste formato:</p>
            <Codigo>{`{
  "statusCode": 404,
  "error": "NaoEncontrada",
  "message": "A fonte não tem o XML desta chave. Nada foi cobrado."
}`}</Codigo>
            <ul className={pv.lista}>
              <li>
                <code>401</code> — chave de API ausente, inválida ou revogada.
              </li>
              <li>
                <code>403</code> — e-mail da conta ainda não confirmado.
              </li>
              <li>
                <code>404</code> — o recurso não existe{' '}
                <i>ou é de outra conta</i> (não confirmamos ids alheios).
              </li>
              <li>
                <code>503</code> — fonte temporariamente fora do ar; respeite o{' '}
                <code>Retry-After</code>.
              </li>
            </ul>
          </Secao>

          <Secao id="recebendo-webhooks" titulo="Recebendo webhooks">
            <p className={pv.p}>
              Cadastre a URL com <code>POST /integracoes/webhooks</code>. A cada
              evento enviamos um <code>POST</code> com este corpo:
            </p>
            <Codigo>{`{
  "id": "evt_6c1f…",            // o mesmo em todas as tentativas: deduplique por ele
  "evento": "lote.concluido",
  "criadoEm": "2026-10-09T13:41:00.000Z",
  "dados": { "jobId": "nfe_…", "status": "CONCLUIDO", "total": 1200,
             "ok": 1183, "notfound": 17, "erro": 0, "cobradoCentavos": 7098 }
}`}</Codigo>
            <ul className={pv.lista}>
              <li>
                <code>lote.concluido</code> — o lote terminou (
                <code>CONCLUIDO</code> ou <code>ERRO</code>), já com o valor
                cobrado.
              </li>
              <li>
                <code>captura.xml_disponivel</code> — chegou o XML completo de
                uma nota da captura (<code>notaId</code>, <code>chave</code>,
                empresa, emitente, valor). Baixe em{' '}
                <code>GET /dfe/notas/{'{notaId}'}/xml</code>.
              </li>
              <li>
                <code>captura.nota_cancelada</code> — o emitente cancelou uma
                nota recebida.
              </li>
            </ul>
            <p className={pv.p}>
              Responda <b>2xx em até 10 segundos</b>. Qualquer outra resposta,
              ou nenhuma, conta como falha: tentamos de novo depois de 1 min, 5
              min, 30 min, 2 h, 6 h, 12 h e 24 h. Só aceitamos URLs{' '}
              <code>https://</code> públicas.
            </p>
            <p className={pv.p}>
              <b>Valide a assinatura</b> antes de confiar no conteúdo. O
              cabeçalho <code>X-BaixaXML-Assinatura</code> traz{' '}
              <code>t=&lt;unix&gt;,v1=&lt;hex&gt;</code>: o HMAC-SHA256 de{' '}
              <code>&lt;t&gt;.&lt;corpo cru&gt;</code> com o segredo do webhook.
              Recuse se o <code>t</code> for de mais de 5 minutos atrás.
            </p>
            <Codigo titulo="Node.js">{`import { createHmac, timingSafeEqual } from 'node:crypto';

function assinaturaValida(corpoCru, cabecalho, segredo) {
  const { t, v1 } = Object.fromEntries(cabecalho.split(',').map((p) => p.split('=')));
  if (Math.abs(Date.now() / 1000 - Number(t)) > 300) return false;
  const esperado = createHmac('sha256', segredo).update(\`\${t}.\${corpoCru}\`).digest('hex');
  return timingSafeEqual(Buffer.from(esperado), Buffer.from(v1));
}`}</Codigo>
            <Codigo titulo="Python">{`import hmac, hashlib, time

def assinatura_valida(corpo_cru: bytes, cabecalho: str, segredo: str) -> bool:
    partes = dict(p.split("=", 1) for p in cabecalho.split(","))
    if abs(time.time() - int(partes["t"])) > 300:
        return False
    esperado = hmac.new(segredo.encode(), partes["t"].encode() + b"." + corpo_cru,
                        hashlib.sha256).hexdigest()
    return hmac.compare_digest(esperado, partes["v1"])`}</Codigo>
          </Secao>

          {GRUPOS_API.map((g) => (
            <Secao key={g.id} id={g.id} titulo={g.titulo}>
              <p className={pv.p}>
                <Rico>{g.descricao}</Rico>
              </p>
              {g.operacoes.map((o) => (
                <OperacaoDoc key={o.id} op={o} />
              ))}
            </Secao>
          ))}
        </main>
      </div>

      <Rodape />
    </div>
  );
}

/** Texto do catálogo: trechos entre crases viram `<code>`. */
function Rico({ children }: { children: string }) {
  return (
    <>
      {children
        .split('`')
        .map((parte, i) =>
          i % 2 === 1 ? <code key={i}>{parte}</code> : parte,
        )}
    </>
  );
}

function Secao({
  id,
  titulo,
  children,
}: {
  id: string;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className={pv.secao}>
      <h2>{titulo}</h2>
      {children}
    </section>
  );
}

function Codigo({ titulo, children }: { titulo?: string; children: string }) {
  return (
    <figure className={css.codigo}>
      {titulo && <figcaption>{titulo}</figcaption>}
      <pre>
        <code>{children}</code>
      </pre>
    </figure>
  );
}

function OperacaoDoc({ op }: { op: Operacao }) {
  return (
    <article id={op.id} className={css.operacao}>
      <h3 className={css.assinatura}>
        <span className={cx(css.metodo, css[op.metodo.toLowerCase()])}>
          {op.metodo}
        </span>
        <code>{op.caminho}</code>
      </h3>
      <p className={css.opTitulo}>{op.titulo}</p>
      <p className={pv.p}>
        <Rico>{op.descricao}</Rico>
      </p>
      {op.cobranca && (
        <p className={css.cobranca}>
          <b>Cobrança:</b> <Rico>{op.cobranca}</Rico>
        </p>
      )}
      {op.parametros && op.parametros.length > 0 && (
        <div className={pv.tabelaMoldura}>
          <table className={pv.tabela}>
            <thead>
              <tr>
                <th>Parâmetro</th>
                <th>Onde</th>
                <th>Descrição</th>
              </tr>
            </thead>
            <tbody>
              {op.parametros.map((p) => (
                <tr key={`${p.em}-${p.nome}`}>
                  <td>
                    <code>{p.nome}</code>
                    {p.obrigatorio && (
                      <span className={css.obrigatorio}> *</span>
                    )}
                  </td>
                  <td>{ONDE[p.em]}</td>
                  <td>
                    <Rico>{p.descricao}</Rico>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Codigo titulo="Exemplo">{curl(op)}</Codigo>
      {op.resposta !== undefined && (
        <Codigo titulo="Resposta">
          {JSON.stringify(op.resposta, null, 2)}
        </Codigo>
      )}
      {op.conteudo && (
        <p className={css.cobranca}>
          Resposta: arquivo <code>{op.conteudo}</code>.
        </p>
      )}
      {op.erros && op.erros.length > 0 && (
        <ul className={pv.lista}>
          {op.erros.map((e) => (
            <li key={`${e.status}-${e.quando}`}>
              <code>{e.status}</code> — <Rico>{e.quando}</Rico>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

const ONDE: Record<Parametro['em'], string> = {
  path: 'caminho',
  query: 'query',
  body: 'corpo JSON',
  form: 'multipart',
};

/** Exemplo em curl a partir do catálogo. */
function curl(op: Operacao): string {
  let caminho = op.caminho;
  for (const p of op.parametros ?? []) {
    if (p.em === 'path')
      caminho = caminho.replace(`{${p.nome}}`, String(p.exemplo ?? p.nome));
  }
  const query = (op.parametros ?? []).filter(
    (p) => p.em === 'query' && p.exemplo !== undefined,
  );
  if (query.length) {
    caminho += `?${query.map((p) => `${p.nome}=${String(p.exemplo)}`).join('&')}`;
  }
  const linhas = [
    `curl${op.metodo === 'GET' ? '' : ` -X ${op.metodo}`} "${URL_BASE}${caminho}"`,
  ];
  if (!op.publica) linhas.push('  -H "x-api-key: bxk_SUA_CHAVE"');
  const corpo = (op.parametros ?? []).filter(
    (p) => p.em === 'body' && p.exemplo !== undefined,
  );
  if (corpo.length) {
    const json = Object.fromEntries(corpo.map((p) => [p.nome, p.exemplo]));
    linhas.push('  -H "content-type: application/json"');
    linhas.push(`  -d '${JSON.stringify(json)}'`);
  }
  for (const p of (op.parametros ?? []).filter((x) => x.em === 'form')) {
    linhas.push(
      p.tipo === 'file'
        ? `  -F "${p.nome}=@arquivo"`
        : `  -F "${p.nome}=valor"`,
    );
  }
  if (op.conteudo === 'application/zip') linhas.push('  -o lote.zip');
  return linhas.join(' \\\n');
}
